import { create } from 'zustand';
import type { SiteConfig, SiteStyle, Page, Component, ComponentType } from '@/types';
import { DEFAULT_COMPONENT_PROPS } from '@/types';

// Simple UUID v4 using crypto.randomUUID when available
function generateId(): string {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return Math.random().toString(36).substring(2) + Date.now().toString(36);
}

interface HistoryEntry {
  siteConfig: SiteConfig;
}

interface BuilderState {
  siteConfig: SiteConfig | null;
  selectedPageId: string | null;
  selectedComponentId: string | null;
  /**
   * D4b selection state — the block currently focused in the BlocksEditor's
   * left pane. Cleared on page switch (the new page's blocks are a different
   * set). Forward-compatible with D5: a scoped AI patch can read this for
   * "edit this block" routing.
   */
  selectedBlockId: string | null;
  isDirty: boolean;
  isSaving: boolean;
  lastSavedAt: Date | null;
  undoStack: HistoryEntry[];
  redoStack: HistoryEntry[];

  // Actions
  setSiteConfig: (config: SiteConfig) => void;
  setSelectedPage: (pageId: string) => void;
  setSelectedComponent: (componentId: string | null) => void;
  setSelectedBlockId: (id: string | null) => void;
  setIsSaving: (value: boolean) => void;
  setLastSavedAt: (date: Date) => void;

  // Page actions
  addPage: (pageId: string, slug: string, title: string) => void;
  removePage: (pageId: string) => void;
  updatePageMeta: (pageId: string, meta: Partial<Pick<Page, 'title' | 'meta_description'>>) => void;

  // Component actions
  addComponent: (pageId: string, type: ComponentType, index?: number) => void;
  removeComponent: (pageId: string, componentId: string) => void;
  updateComponentProps: (pageId: string, componentId: string, props: Record<string, unknown>) => void;
  reorderComponents: (pageId: string, components: Component[]) => void;

  // Theme/style actions
  updateTheme: (theme: Partial<SiteConfig['theme']>) => void;
  updateStyle: (style: Partial<SiteStyle>) => void;
  /**
   * Mirror an in-session logo change into the in-memory `siteConfig.brand.logo_url` so the
   * brand-first header preview (StudioBuilder) updates INSTANTLY. The durable write is always
   * saveStyle → site_style_settings.logo_url (what GET /site-config resolves back into
   * brand.logo_url on reload, and what the live <Navigation> renders); this just keeps the
   * preview in sync without a refetch. Does NOT mark the config dirty — persistence is the
   * style row's job, not the config blob's.
   */
  setBrandLogo: (logoUrl: string) => void;

  // Navigation / footer
  updateNavigation: (nav: SiteConfig['navigation']) => void;
  updateFooter: (footer: SiteConfig['footer']) => void;

  // History
  undo: () => void;
  redo: () => void;

  // Helpers
  getSelectedPage: () => Page | null;
  getSelectedComponent: () => Component | null;

  // Session reset
  reset: () => void;
}

function pushHistory(state: BuilderState): Pick<BuilderState, 'undoStack' | 'redoStack'> {
  if (!state.siteConfig) return { undoStack: state.undoStack, redoStack: state.redoStack };
  const entry: HistoryEntry = {
    siteConfig: JSON.parse(JSON.stringify(state.siteConfig)),
  };
  const undoStack = [...state.undoStack, entry].slice(-50); // cap at 50
  return { undoStack, redoStack: [] };
}

export const useBuilderStore = create<BuilderState>()((set, get) => ({
  siteConfig: null,
  selectedPageId: null,
  selectedComponentId: null,
  selectedBlockId: null,
  isDirty: false,
  isSaving: false,
  lastSavedAt: null,
  undoStack: [],
  redoStack: [],

  setSiteConfig: (config) => {
    // Defensive: the gateway's `/site-config` (used by the builder) returns the
    // raw site_configs.config blob, which may not include a `pages` array
    // (pages live in site_pages). getSiteConfig grafts /pages on top, but if
    // that fetch ever fails or the shape changes, this kept-empty fallback
    // prevents an uncaught `config.pages[0]` crash that white-screens the editor.
    const normalized = { ...config, pages: config.pages ?? [] };
    // Preserve the current page selection across config refreshes as long as the
    // selected page still exists. The autosave round-trip re-runs this hook
    // (saveMutation.onSuccess → setQueryData → query.data → setSiteConfig), and
    // so do refetches — without this guard, EVERY site-config save silently
    // bounced the editor back to the home page mid-edit. Concretely: adding a
    // page selected it, then ~2s later the autosave round-trip reset the
    // selection to home, so the next AI build landed on the wrong page. Only fall
    // back to the first page on the initial load (nothing selected yet) or when
    // the selected page was removed from the config.
    const { selectedPageId } = get();
    const keepSelected =
      selectedPageId != null && normalized.pages.some((p) => p.id === selectedPageId);
    // Initial selection prefers the HOME page — never the auto-seeded Privacy/Terms pages, which
    // sort first on a fresh site and would otherwise be what the preview opens on. Fall back to a
    // home-ish slug, then the first page.
    const initialPage =
      normalized.pages.find(
        (p) => (p as { is_home_page?: boolean | number }).is_home_page === true || (p as { is_home_page?: boolean | number }).is_home_page === 1,
      ) ??
      normalized.pages.find((p) => {
        const s = (p as { slug?: string }).slug ?? '';
        return s === '/' || s === '' || s === 'home';
      }) ??
      normalized.pages[0];
    set({
      siteConfig: normalized,
      selectedPageId: keepSelected ? selectedPageId : initialPage?.id ?? null,
      selectedBlockId: null,
      isDirty: false,
      undoStack: [],
      redoStack: [],
    });
  },

  setSelectedPage: (pageId) => {
    set({ selectedPageId: pageId, selectedComponentId: null, selectedBlockId: null });
  },

  setSelectedComponent: (componentId) => {
    set({ selectedComponentId: componentId });
  },

  setSelectedBlockId: (id) => {
    set({ selectedBlockId: id });
  },

  setIsSaving: (value) => set({ isSaving: value }),
  setLastSavedAt: (date) => set({ lastSavedAt: date, isDirty: false }),

  addPage: (pageId, slug, title) => {
    const state = get();
    if (!state.siteConfig) return;
    const history = pushHistory(state);
    const newPage: Page = {
      id: pageId,
      slug,
      title,
      components: [],
    };
    const newConfig = {
      ...state.siteConfig,
      pages: [...state.siteConfig.pages, newPage],
    };
    set({ ...history, siteConfig: newConfig, isDirty: true, selectedPageId: newPage.id });
  },

  removePage: (pageId) => {
    const state = get();
    if (!state.siteConfig) return;
    const history = pushHistory(state);
    const pages = state.siteConfig.pages.filter((p) => p.id !== pageId);
    const newConfig = { ...state.siteConfig, pages };
    const newSelectedId = state.selectedPageId === pageId ? pages[0]?.id ?? null : state.selectedPageId;
    set({ ...history, siteConfig: newConfig, isDirty: true, selectedPageId: newSelectedId });
  },

  updatePageMeta: (pageId, meta) => {
    const state = get();
    if (!state.siteConfig) return;
    const history = pushHistory(state);
    const pages = state.siteConfig.pages.map((p) =>
      p.id === pageId ? { ...p, ...meta } : p
    );
    set({ ...history, siteConfig: { ...state.siteConfig, pages }, isDirty: true });
  },

  addComponent: (pageId, type, index) => {
    const state = get();
    if (!state.siteConfig) return;
    const history = pushHistory(state);
    const newComponent: Component = {
      id: generateId(),
      type,
      props: { ...DEFAULT_COMPONENT_PROPS[type] },
      order: 0,
    };
    const pages = state.siteConfig.pages.map((p) => {
      if (p.id !== pageId) return p;
      let components = [...p.components];
      if (index !== undefined) {
        components.splice(index, 0, newComponent);
      } else {
        components.push(newComponent);
      }
      components = components.map((c, i) => ({ ...c, order: i }));
      return { ...p, components };
    });
    set({
      ...history,
      siteConfig: { ...state.siteConfig, pages },
      isDirty: true,
      selectedComponentId: newComponent.id,
    });
  },

  removeComponent: (pageId, componentId) => {
    const state = get();
    if (!state.siteConfig) return;
    const history = pushHistory(state);
    const pages = state.siteConfig.pages.map((p) => {
      if (p.id !== pageId) return p;
      const components = p.components
        .filter((c) => c.id !== componentId)
        .map((c, i) => ({ ...c, order: i }));
      return { ...p, components };
    });
    set({
      ...history,
      siteConfig: { ...state.siteConfig, pages },
      isDirty: true,
      selectedComponentId: state.selectedComponentId === componentId ? null : state.selectedComponentId,
    });
  },

  updateComponentProps: (pageId, componentId, props) => {
    const state = get();
    if (!state.siteConfig) return;
    const history = pushHistory(state);
    const pages = state.siteConfig.pages.map((p) => {
      if (p.id !== pageId) return p;
      const components = p.components.map((c) =>
        c.id === componentId ? { ...c, props: { ...c.props, ...props } } : c
      );
      return { ...p, components };
    });
    set({ ...history, siteConfig: { ...state.siteConfig, pages }, isDirty: true });
  },

  reorderComponents: (pageId, components) => {
    const state = get();
    if (!state.siteConfig) return;
    const history = pushHistory(state);
    const pages = state.siteConfig.pages.map((p) => {
      if (p.id !== pageId) return p;
      return { ...p, components: components.map((c, i) => ({ ...c, order: i })) };
    });
    set({ ...history, siteConfig: { ...state.siteConfig, pages }, isDirty: true });
  },

  updateTheme: (theme) => {
    const state = get();
    if (!state.siteConfig) return;
    const history = pushHistory(state);
    set({
      ...history,
      siteConfig: {
        ...state.siteConfig,
        theme: { ...state.siteConfig.theme, ...theme },
      },
      isDirty: true,
    });
  },

  updateStyle: (style) => {
    const state = get();
    if (!state.siteConfig) return;
    const history = pushHistory(state);
    set({
      ...history,
      siteConfig: {
        ...state.siteConfig,
        style: { ...state.siteConfig.style, ...style },
      },
      isDirty: true,
    });
  },

  setBrandLogo: (logoUrl) => {
    const state = get();
    if (!state.siteConfig) return;
    const cfg = state.siteConfig as SiteConfig & { brand?: { logo_url?: string | null } };
    // In-memory mirror only (no history, no isDirty): the brand-first preview reads
    // siteConfig.brand.logo_url, and saveStyle has already persisted the durable copy.
    set({ siteConfig: { ...cfg, brand: { ...(cfg.brand ?? {}), logo_url: logoUrl } } });
  },

  updateNavigation: (navigation) => {
    const state = get();
    if (!state.siteConfig) return;
    const history = pushHistory(state);
    set({ ...history, siteConfig: { ...state.siteConfig, navigation }, isDirty: true });
  },

  updateFooter: (footer) => {
    const state = get();
    if (!state.siteConfig) return;
    const history = pushHistory(state);
    set({ ...history, siteConfig: { ...state.siteConfig, footer }, isDirty: true });
  },

  undo: () => {
    const state = get();
    if (state.undoStack.length === 0) return;
    const prevEntry = state.undoStack[state.undoStack.length - 1];
    const redoEntry: HistoryEntry = state.siteConfig
      ? { siteConfig: JSON.parse(JSON.stringify(state.siteConfig)) }
      : prevEntry;
    set({
      siteConfig: prevEntry.siteConfig,
      undoStack: state.undoStack.slice(0, -1),
      redoStack: [...state.redoStack, redoEntry],
      isDirty: true,
    });
  },

  redo: () => {
    const state = get();
    if (state.redoStack.length === 0) return;
    const nextEntry = state.redoStack[state.redoStack.length - 1];
    const undoEntry: HistoryEntry = state.siteConfig
      ? { siteConfig: JSON.parse(JSON.stringify(state.siteConfig)) }
      : nextEntry;
    set({
      siteConfig: nextEntry.siteConfig,
      undoStack: [...state.undoStack, undoEntry],
      redoStack: state.redoStack.slice(0, -1),
      isDirty: true,
    });
  },

  getSelectedPage: () => {
    const state = get();
    if (!state.siteConfig || !state.selectedPageId) return null;
    return state.siteConfig.pages.find((p) => p.id === state.selectedPageId) ?? null;
  },

  getSelectedComponent: () => {
    const state = get();
    if (!state.siteConfig || !state.selectedPageId || !state.selectedComponentId) return null;
    const page = state.siteConfig.pages.find((p) => p.id === state.selectedPageId);
    if (!page) return null;
    return page.components.find((c) => c.id === state.selectedComponentId) ?? null;
  },

  reset: () =>
    set({
      siteConfig: null,
      selectedPageId: null,
      selectedComponentId: null,
      selectedBlockId: null,
      isDirty: false,
      isSaving: false,
      lastSavedAt: null,
      undoStack: [],
      redoStack: [],
    }),
}));
